import React, { useEffect, useState } from 'react';
import { useStore } from '../../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Package, 
  ShoppingCart, 
  Users, 
  CurrencyEur,
  TrendUp,
  TrendDown,
  ArrowRight,
  Plus
} from '@phosphor-icons/react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardHome = () => {
  const { currentStore } = useStore();
  const { storeSlug } = useParams();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Period comparison state
  const today = new Date();
  const toIso = (d) => d.toISOString().slice(0, 10);
  const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
  const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const [rangeA, setRangeA] = useState({ start: toIso(startOfMonth(today)), end: toIso(endOfMonth(today)) });
  const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const [rangeB, setRangeB] = useState({ start: toIso(startOfMonth(lastMonth)), end: toIso(endOfMonth(lastMonth)) });
  const [showCompare, setShowCompare] = useState(false);
  const [statsA, setStatsA] = useState(null);
  const [statsB, setStatsB] = useState(null);
  const [loadingCompare, setLoadingCompare] = useState(false);

  // Detect if we're inside the store-specific admin
  const isStoreAdmin = location.pathname.includes('/admin');
  const basePath = isStoreAdmin && storeSlug ? `/store/${storeSlug}/admin` : '/dashboard';
  const accentColor = isStoreAdmin 
    ? (currentStore?.theme_config?.colors?.primary || '#c2185b') 
    : (currentStore?.identity?.primary_color || currentStore?.theme_config?.colors?.primary || '#0052FF');

  useEffect(() => {
    const fetchStats = async () => {
      if (!currentStore) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/stores/${currentStore.id}/stats?period=30d`, {
          withCredentials: true
        });
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [currentStore]);

  const fetchComparison = async () => {
    if (!currentStore) return;
    setLoadingCompare(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API_URL}/api/stores/${currentStore.id}/stats`, {
          params: { start_date: rangeA.start, end_date: rangeA.end + 'T23:59:59' },
          withCredentials: true,
        }),
        axios.get(`${API_URL}/api/stores/${currentStore.id}/stats`, {
          params: { start_date: rangeB.start, end_date: rangeB.end + 'T23:59:59' },
          withCredentials: true,
        }),
      ]);
      setStatsA(a.data);
      setStatsB(b.data);
    } catch (e) {
      console.error('compare failed', e);
    } finally { setLoadingCompare(false); }
  };

  useEffect(() => { if (showCompare) fetchComparison(); /* eslint-disable-next-line */ }, [showCompare, rangeA, rangeB]);

  if (!currentStore) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mb-6">
          <Package size={48} className="text-slate-400" />
        </div>
        <h2 className="font-cabinet text-2xl font-bold mb-2">Bienvenue sur SHOPIZY</h2>
        <p className="text-slate-500 mb-6 max-w-md">
          Créez votre première boutique pour commencer à vendre en ligne.
        </p>
        <Button 
          onClick={() => navigate('/admin/products')} 
          className="bg-[#0052FF] hover:bg-[#0040CC]"
          data-testid="create-first-store"
        >
          <Plus className="mr-2" size={20} />
          Créer ma boutique
        </Button>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Chiffre d\'affaires',
      value: stats?.total_revenue?.toFixed(2) || '0.00',
      suffix: '€',
      icon: CurrencyEur,
      color: 'bg-emerald-500',
      trend: '+12%',
      trendUp: true
    },
    {
      title: 'Commandes',
      value: stats?.total_orders || 0,
      icon: ShoppingCart,
      color: 'bg-blue-500',
      trend: '+8%',
      trendUp: true
    },
    {
      title: 'Produits',
      value: stats?.products_count || 0,
      icon: Package,
      color: 'bg-purple-500'
    },
    {
      title: 'Marge moyenne',
      value: stats?.avg_margin?.toFixed(1) || '0',
      suffix: '%',
      icon: TrendUp,
      color: 'bg-amber-500'
    }
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-home">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-cabinet text-3xl font-bold tracking-tight">Tableau de bord</h1>
          <p className="text-slate-500 mt-1">{currentStore?.name} - Aperçu des 30 derniers jours</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate(`${basePath}/products`)} data-testid="go-to-products">
            <Package className="mr-2" size={18} />
            Produits
          </Button>
          <Button style={{ backgroundColor: accentColor }} className="hover:opacity-90 text-white" onClick={() => navigate(`${basePath}/products/new`)} data-testid="add-product-btn">
            <Plus className="mr-2" size={18} />
            Nouveau produit
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <Card key={index} className="dashboard-card animate-fade-in-up" style={{ animationDelay: `${index * 100}ms` }}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium">{stat.title}</p>
                  <p className="text-3xl font-bold mt-2 font-cabinet tracking-tight">
                    {stat.value}{stat.suffix}
                  </p>
                  {stat.trend && (
                    <div className={`flex items-center gap-1 mt-2 text-sm ${stat.trendUp ? 'text-emerald-600' : 'text-red-600'}`}>
                      {stat.trendUp ? <TrendUp size={16} /> : <TrendDown size={16} />}
                      <span>{stat.trend}</span>
                    </div>
                  )}
                </div>
                <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center`}>
                  <stat.icon size={24} className="text-white" weight="fill" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Period comparison */}
      <Card className="dashboard-card" data-testid="period-compare">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-semibold">📊 Comparaison de périodes</CardTitle>
          <button
            onClick={() => setShowCompare(s => !s)}
            className="text-[12px] px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50"
            data-testid="toggle-compare"
          >
            {showCompare ? 'Masquer' : 'Afficher'}
          </button>
        </CardHeader>
        {showCompare && (
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="border rounded-lg p-3 bg-blue-50/40 border-blue-200">
                <p className="text-[11px] uppercase font-bold text-blue-700 mb-2">📅 Période A</p>
                <div className="flex gap-2 items-center text-sm">
                  <input type="date" value={rangeA.start} onChange={e => setRangeA({ ...rangeA, start: e.target.value })}
                         className="border rounded px-2 py-1 text-[13px]" data-testid="rangeA-start" />
                  <span className="text-slate-400">→</span>
                  <input type="date" value={rangeA.end} onChange={e => setRangeA({ ...rangeA, end: e.target.value })}
                         className="border rounded px-2 py-1 text-[13px]" data-testid="rangeA-end" />
                </div>
                {statsA && (
                  <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                    <div><p className="text-[10px] text-blue-700/70">CA</p><p className="font-bold text-blue-700">{(statsA.total_revenue || 0).toFixed(0)}€</p></div>
                    <div><p className="text-[10px] text-blue-700/70">Cmd.</p><p className="font-bold text-blue-700">{statsA.total_orders || 0}</p></div>
                    <div><p className="text-[10px] text-blue-700/70">Panier</p><p className="font-bold text-blue-700">{(statsA.avg_order_value || 0).toFixed(0)}€</p></div>
                  </div>
                )}
              </div>
              <div className="border rounded-lg p-3 bg-orange-50/40 border-orange-200">
                <p className="text-[11px] uppercase font-bold text-orange-700 mb-2">📅 Période B</p>
                <div className="flex gap-2 items-center text-sm">
                  <input type="date" value={rangeB.start} onChange={e => setRangeB({ ...rangeB, start: e.target.value })}
                         className="border rounded px-2 py-1 text-[13px]" data-testid="rangeB-start" />
                  <span className="text-slate-400">→</span>
                  <input type="date" value={rangeB.end} onChange={e => setRangeB({ ...rangeB, end: e.target.value })}
                         className="border rounded px-2 py-1 text-[13px]" data-testid="rangeB-end" />
                </div>
                {statsB && (
                  <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                    <div><p className="text-[10px] text-orange-700/70">CA</p><p className="font-bold text-orange-700">{(statsB.total_revenue || 0).toFixed(0)}€</p></div>
                    <div><p className="text-[10px] text-orange-700/70">Cmd.</p><p className="font-bold text-orange-700">{statsB.total_orders || 0}</p></div>
                    <div><p className="text-[10px] text-orange-700/70">Panier</p><p className="font-bold text-orange-700">{(statsB.avg_order_value || 0).toFixed(0)}€</p></div>
                  </div>
                )}
              </div>
            </div>
            {/* Quick presets */}
            <div className="flex flex-wrap gap-1.5 text-[12px]">
              <span className="text-slate-500 mr-1">Raccourcis :</span>
              <button onClick={() => { setRangeA({ start: toIso(startOfMonth(today)), end: toIso(endOfMonth(today)) }); setRangeB({ start: toIso(startOfMonth(lastMonth)), end: toIso(endOfMonth(lastMonth)) }); }}
                      className="px-2 py-1 rounded border bg-slate-50 hover:bg-slate-100" data-testid="preset-mom">Mois en cours vs Mois précédent</button>
              <button onClick={() => { const y = today.getFullYear(); const ly = y - 1; setRangeA({ start: `${y}-01-01`, end: toIso(today) }); setRangeB({ start: `${ly}-01-01`, end: `${ly}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}` }); }}
                      className="px-2 py-1 rounded border bg-slate-50 hover:bg-slate-100" data-testid="preset-yoy">Cette année vs An dernier (à date)</button>
              <button onClick={() => { const y = today.getFullYear(); setRangeA({ start: `${y}-01-01`, end: `${y}-12-31` }); setRangeB({ start: `${y - 1}-01-01`, end: `${y - 1}-12-31` }); }}
                      className="px-2 py-1 rounded border bg-slate-50 hover:bg-slate-100" data-testid="preset-yfull">Année complète</button>
            </div>
            {/* Overlay chart */}
            {(statsA || statsB) && (() => {
              // Merge daily data: align by day-of-period offset for cross-year comparison
              const a = (statsA?.daily_data || []).map((d, i) => ({ idx: i, label: d.date, A: d.revenue }));
              const b = (statsB?.daily_data || []).map((d, i) => ({ idx: i, label: d.date, B: d.revenue }));
              const maxLen = Math.max(a.length, b.length);
              const merged = [];
              for (let i = 0; i < maxLen; i++) {
                merged.push({
                  idx: i + 1,
                  jour: `J${i + 1}`,
                  A: a[i]?.A || 0,
                  labelA: a[i]?.label,
                  B: b[i]?.B || 0,
                  labelB: b[i]?.label,
                });
              }
              return (
                <div className="h-[280px]">
                  {loadingCompare ? (
                    <div className="h-full flex items-center justify-center text-slate-400">Chargement…</div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={merged}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis dataKey="jour" stroke="#6B7280" fontSize={11} />
                        <YAxis stroke="#6B7280" fontSize={11} />
                        <Tooltip
                          contentStyle={{ backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                          formatter={(value, name, props) => {
                            const label = name === 'A' ? props.payload.labelA : props.payload.labelB;
                            return [`${(value || 0).toFixed(2)} €`, `${name} (${label || '—'})`];
                          }}
                        />
                        <Line type="monotone" dataKey="A" name="Période A" stroke="#0052FF" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="B" name="Période B" stroke="#F59E0B" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>
              );
            })()}
          </CardContent>
        )}
      </Card>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <Card className="dashboard-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold">Évolution du CA</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {stats?.daily_data && stats.daily_data.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={stats.daily_data}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={accentColor} stopOpacity={0.2}/>
                        <stop offset="95%" stopColor={accentColor} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(val) => val.substring(5)}
                      stroke="#6B7280"
                      fontSize={12}
                    />
                    <YAxis stroke="#6B7280" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}
                      formatter={(value) => [`${value.toFixed(2)} €`, 'CA']}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke={accentColor} 
                      strokeWidth={2}
                      fill="url(#colorRevenue)" 
                      animationDuration={1500}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400">
                  Pas encore de données
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Orders Chart */}
        <Card className="dashboard-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold">Commandes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {stats?.daily_data && stats.daily_data.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={stats.daily_data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(val) => val.substring(5)}
                      stroke="#6B7280"
                      fontSize={12}
                    />
                    <YAxis stroke="#6B7280" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => [value, 'Commandes']}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="orders" 
                      stroke="#8B5CF6" 
                      strokeWidth={2}
                      dot={{ fill: '#8B5CF6', strokeWidth: 2 }}
                      animationDuration={1500}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400">
                  Pas encore de données
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Products */}
      <Card className="dashboard-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg font-semibold">Produits les plus vendus</CardTitle>
          <Link to={`${basePath}/products`} style={{ color: accentColor }} className="text-sm hover:underline flex items-center gap-1">
            Voir tout <ArrowRight size={14} />
          </Link>
        </CardHeader>
        <CardContent>
          {stats?.top_products && stats.top_products.length > 0 ? (
            <div className="space-y-4">
              {stats.top_products.map((product, index) => (
                <div key={product._id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div className="flex items-center gap-4">
                    <span className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-sm font-medium">
                      {index + 1}
                    </span>
                    <span className="font-medium">{product.name}</span>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">{product.revenue?.toFixed(2)} €</p>
                    <p className="text-sm text-slate-500">{product.total_sold} vendus</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-slate-400">
              Aucune vente pour le moment
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardHome;
